// react and external-libs
import React, { useCallback, useEffect, useMemo, useState } from 'react';
// MUI
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { Delete, Edit, NotificationImportantRounded } from '@mui/icons-material';
// redux
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { fetchWorkflowByIdSlice, updateWorkflow } from '../../../app/store/workflowSlice';
import { acceptAssessment, rejectAssessment } from '../../../services/workflowThunks';
import { deleteAssessment } from '../../../app/store/assessmentSlice';
import { isAuditLogAfterLastView } from '../../../utils/auditLogUtils';
// interface
import { SiteCloudStatus, Survey } from '../../../interfaces/survey.interface';
import { Workflow } from '../../../interfaces/workflow.interface';
import User, { Company } from '../../../interfaces/users.interface';
import { Assessment, AssessmentSurvey } from '../../../interfaces/assessment.interface';
// services
import {
  fetchAssessment,
  getSiteCloudStatus,
  updateAssessmentSurvey,
} from '../../../services/assessments';

// components
import WorkflowStatus from '../WorkflowStatus';
import AssessmentSurveyRow from './AssessmentSurveyRow';
import AssessmentForm from '../../Assessments/NewAssessmentModal';
import AssessorAssessmentComments from '../../Comments/AssessorAssessmentComments';
import { Accordion, AccordionSummary } from '../../Layout/Collapsible';
import { DataCell, DataRow, DataRowActions } from '../../Tables/DataList';
import { AcceptAssessmentDialog, RejectAssessmentDialog, DeleteAssessmentDialog } from './dialogs';
import { USER_TYPES } from '../../../services/user';
import { COMPANY_TYPES } from '../../../services/vendor';

type AssessmentDataRowProps = {
  company: Company;
  workflow: Workflow;
  vendorCompany?: Company;
  userCanAdminCompany: boolean;
};

const AssessmentDataRow = (props: AssessmentDataRowProps) => {
  const { userCanAdminCompany, company, workflow, vendorCompany } = props;
  // redux
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const { assessmentAuditLog, assessmentLocalStorageItems } = useAppSelector((state) => state.assessments);
  // states
  const [expanded, setExpanded] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [cAssessment, setcAssessment] = useState<Assessment | undefined>(undefined);
  const [surveys, setFilteredSurveys] = useState<Survey[]>([]);
  const [assessedCompany, setAssessedCompany] = useState<Company | undefined>(undefined);
  // modal states
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [deleteAssessmentOpen, setDeleteAssessmentOpen] = useState(false);
  const [acceptAssessmentOpen, setAcceptAssessmentOpen] = useState(false);
  const [rejectAssessmentOpen, setRejectAssessmentOpen] = useState(false);
  // local variables
  const currAssSurvey = useMemo(
    () => (cAssessment?.surveys as AssessmentSurvey[])?.find((assSur) => assSur?.id === workflow?.assessmentSurvey),
    [cAssessment?.surveys, workflow.assessmentSurvey],
  );
  // simple/short functions
  const handleAcceptAssessmentOpen = useCallback(() => setAcceptAssessmentOpen(true), []);
  const handleAcceptAssessmentClose = useCallback(() => setAcceptAssessmentOpen(false), []);
  const handleRejectAssessmentOpen = useCallback(() => setRejectAssessmentOpen(true), []);
  const handleRejectAssessmentClose = useCallback(() => setRejectAssessmentOpen(false), []);
  const handleDeleteAssessmentOpen = useCallback(() => setDeleteAssessmentOpen(true), []);
  const handleDeleteAssessmentClose = useCallback(() => setDeleteAssessmentOpen(false), []);

  const handleAssessmentDeleteSubmit = useCallback(
    (assessmentId: number) => {
      dispatch(deleteAssessment(assessmentId));
      if (workflow) dispatch(updateWorkflow({ adminOverride: true, id: workflow.id, assessment: null, assessmentSurvey: null, assessor: null, assignedAssessor: null, status: 'submittedForAssessment' }));
      handleDeleteAssessmentClose();
    },
    [dispatch],
  );

  const handleAcceptAssessment = useCallback(
    async () => {
      try {
        const result = await dispatch(acceptAssessment(workflow.id)).unwrap();
        // Use the full assessment object from the response
        if (result.assessment) {
          setcAssessment(result.assessment);
        }
      } catch (error) {
        console.error('Failed to accept assessment:', error);
      } finally {
        dispatch(fetchWorkflowByIdSlice(workflow.id));
        handleAcceptAssessmentClose();
      }
    },
    [workflow.id, dispatch],
  );

  const handleRejectAssessment = useCallback(
    async () => {
      try {
        await dispatch(rejectAssessment(workflow.id)).unwrap();
        // Clear local assessment state since it's been deleted
        setcAssessment(undefined);
      } catch (error) {
        console.error('Failed to reject assessment:', error);
      }
      handleRejectAssessmentClose();
    },
    [workflow.id, dispatch],
  );

  const updateAssessmentFromAssessmentSurvey = useCallback(
    (assessmentSurvey: AssessmentSurvey) => {
      const assessmentSurveyIndex = (cAssessment?.surveys as AssessmentSurvey[]).findIndex(
        (assSur) => assSur.id === assessmentSurvey.id,
      );
      if (assessmentSurveyIndex === -1) return;
      const newAssessment = JSON.parse(JSON.stringify(cAssessment));
      newAssessment.surveys[assessmentSurveyIndex] = assessmentSurvey;
      setcAssessment(newAssessment);
    },
    [cAssessment],
  );

  const surveyRow = useMemo(
    () => (assessmentSurvey: AssessmentSurvey) => {
      if (!assessmentSurvey || (company.type === COMPANY_TYPES.ASSESSOR && !assessedCompany) || !surveys) return;
      // if (!assessmentSurvey || (company.type === COMPANY_TYPES.ASSESSOR && !assessedCompany) || !workflow?.bpSurvey) return;
      const theCompany = assessedCompany ? assessedCompany : company;

      const survey = surveys.find((sur) =>
        typeof assessmentSurvey.survey === 'number'
          ? sur.id === assessmentSurvey.survey
          : sur.id === assessmentSurvey.survey.id!,
      );
      if (!survey) return;

      // Full-company lookup first (v1 passes complete objects); the embedded slim detail only backfills v2's lean company.
      const location = (survey.site ? theCompany.locations?.find((loc) => loc.id === survey.site) : undefined)
        ?? survey.siteDetail ?? undefined;
      const app = (survey.application ? theCompany.companyApplications?.find((napp) => napp.application.id === survey.application) : undefined)
        ?? survey.companyApplicationDetail ?? undefined;

      return (
        <AssessmentSurveyRow
          key={assessmentSurvey.id}
          companyApplication={app}
          site={location}
          company={theCompany}
          assessment={cAssessment!}
          survey={survey}
          assessmentSurvey={assessmentSurvey}
          updateAssessmentFromAssessmentSurvey={updateAssessmentFromAssessmentSurvey}
        />
      );
    },
    [company, assessedCompany, surveys],
  );

  // Assessor Comment Modal Logic
  const [showAssessmentComments, setShowAssessmentComments] = useState(false);
  const [assessmentComment, setAssessmentComment] = useState<string>('');
  useEffect(() => {
    if (currAssSurvey?.assessorComment) {
      setAssessmentComment(currAssSurvey.assessorComment);
    }
  }, [currAssSurvey?.assessorComment]);

  const handleSetShowAssessmentComments = useCallback((comment: string) => {
    setAssessmentComment(comment);
    setShowAssessmentComments(true);
  }, [setAssessmentComment, setShowAssessmentComments]);

  const handleAssessmentComment = useCallback(async (assessorComment: string) => {
    if (currAssSurvey !== undefined) {
      try {
        await updateAssessmentSurvey({ ...currAssSurvey, assessorComment });
        setAssessmentComment(assessorComment);
      } catch (e) {
        console.error('failed to update assessmentSurvey');
      }
    }
  }, [currAssSurvey]);

  // fetching assessment
  useEffect(() => {
    if (workflow?.assessment) {
      fetchAssessment(workflow.assessment!, { excludeQuestionAnswers: true }).then((newAssessment) => setcAssessment(newAssessment));
    }
  }, [workflow.assessment]);

  // updating assessment
  useEffect(() => {
    const filteredSurveys = (cAssessment?.surveys as AssessmentSurvey[])?.map((sur) => sur.survey as Survey);
    setFilteredSurveys(filteredSurveys ? filteredSurveys.filter(sur => sur) : []);
  }, [cAssessment]);

  // setting of vendor company
  useEffect(() => {
    setAssessedCompany(vendorCompany);
  }, [vendorCompany]);

  // show alert
  useEffect(() => {
    if (!cAssessment?.surveys.length) return;
    if (!assessmentAuditLog.length) {
      setShowAlert(false);
      return;
    }
    const curAssessmentSurveys = (cAssessment?.surveys as AssessmentSurvey[]).filter(
      (assSur) => ['Complete', 'Remediation'].indexOf(assSur.status) === -1,
    );
    const assessmetnSurveyIds = curAssessmentSurveys.map((assSur) => assSur.id);
    // TPE-2919: only light the alert when there is activity newer than the viewer's last-view
    // marker for that survey. Once the questionnaire is viewed the marker advances to now, so the
    // next poll finds nothing newer and the bell clears (previously it lit on any activity at all).
    setShowAlert(
      assessmentAuditLog.find((assAl) => {
        if (assessmetnSurveyIds.indexOf(assAl.assessmentSurvey) === -1) return false;
        const lastView = assessmentLocalStorageItems?.find(
          (item) => item.assessmentSurvey === assAl.assessmentSurvey,
        )?.date;
        return assAl.auditLogs.filter(
          (al) => (al.user as User).id !== user?.id && isAuditLogAfterLastView(al.createdAt, lastView),
        ).length > 0;
      }) !== undefined,
    );
  }, [assessmentAuditLog, assessmentLocalStorageItems, cAssessment]);

  const [siteCloudStatus, setSiteCloudStatus] = useState<SiteCloudStatus>({ site: false, cloud: false, both: false });

  const fetchSiteCloudStatus = async (baselineId: number) => {
    const siteCloundData = await getSiteCloudStatus(baselineId);
    setSiteCloudStatus(siteCloundData);
  };

  useEffect(() => {
    if (workflow?.baseline) fetchSiteCloudStatus(workflow.baseline);
  }, [workflow?.baseline]);

  if (!cAssessment) {
    return null;
  }

  return (
    <>
    <AssessorAssessmentComments
      canEdit={user && user.type === COMPANY_TYPES.ASSESSOR && currAssSurvey && ['Complete', 'Remediation'].indexOf(currAssSurvey.status) === -1}
      originalComment={assessmentComment}
      handleAssessmentComment={handleAssessmentComment}
      setShowAssessmentComments={setShowAssessmentComments}
      showAssessmentComments={showAssessmentComments}
    />
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary>
        <DataRow>
          <DataCell xs={4}>
            {showAlert && (
              <NotificationImportantRounded
                color='warning'
                sx={{ position: 'absolute', top: '0', left: '0', width: '.8em' }}
              />
            )}
            {company.type === COMPANY_TYPES.VENDOR
              ? (cAssessment?.assessor as Company)?.name
              : (cAssessment?.company as Company)?.name}
          </DataCell>
          <DataCell xs={3}>{cAssessment?.surveys.length}</DataCell>
          <DataCell xs={2}>
            <WorkflowStatus workflowDisplayStatusObject={workflow.displayStatus} workflowDisplayType={workflow.workflowType} />
          </DataCell>
          <DataCell xs={2}>
            {company.type === COMPANY_TYPES.VENDOR && userCanAdminCompany && cAssessment?.status === 'Rejected' && (
              <Button
                variant='outlined'
                sx={{ width: '150px' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setAssessmentModalOpen(true);
                }}
              >
                Reassign
              </Button>
            )}
            {['Assigned', 'Rejected'].indexOf(cAssessment?.status!) === -1 && (
              <Button sx={{ maxWidth: '100%', width: '205px', mt: 1 }} size='small' variant='outlined' onClick={(e) => {e.stopPropagation(); handleSetShowAssessmentComments(assessmentComment ?? currAssSurvey?.assessorComment);}}>Assessor Summary</Button>
            )}
            {company.type === COMPANY_TYPES.ASSESSOR && (
              <>
                {cAssessment?.status === 'Assigned' && (
                  <Stack spacing={1} alignItems='center'>
                    <Button
                      variant='outlined'
                      sx={{ width: '150px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcceptAssessmentOpen();
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      variant='outlined'
                      color='error'
                      sx={{ width: '150px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRejectAssessmentOpen();
                      }}
                    >
                      Reject
                    </Button>
                  </Stack>
                )}
              </>
            )}
            {company.type !== COMPANY_TYPES.ASSESSOR &&
              !(company.type === COMPANY_TYPES.VENDOR && userCanAdminCompany && cAssessment?.status === 'Rejected') && <></>}
          </DataCell>
          <DataCell xs={1}>
            {(userCanAdminCompany &&
              user?.type === USER_TYPES.VENDOR_ADMIN &&
              ['Assigned', 'Rejected'].indexOf(cAssessment?.status!) !== -1) ||
            user?.type === USER_TYPES.TPN_ADMIN ? (
              <DataRowActions>
                <IconButton
                  size='small'
                  name='Delete Assessment'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteAssessmentOpen();
                  }}
                >
                  <Delete />
                </IconButton>
                {['Assigned', 'Rejected'].indexOf(cAssessment?.status!) !== -1 && (
                  <IconButton
                    size='small'
                    name='Edit Assessment'
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAssessmentModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                )}
              </DataRowActions>
              ) : (
              <Typography align='center'>-</Typography>
              )}
          </DataCell>
        </DataRow>
      </AccordionSummary>
      <DataRow header>
        <DataCell xs={2}>Type</DataCell>
        <DataCell xs={2}>Name</DataCell>
        <DataCell xs={2}>Scope</DataCell>
        <DataCell xs={2}>Type</DataCell>
        <DataCell xs={2}>Status</DataCell>
        <DataCell xs={2} textAlign='center'>
          Actions
        </DataCell>
      </DataRow>
      {(cAssessment?.surveys as AssessmentSurvey[]).map((survey) => surveyRow(survey))}
      <DeleteAssessmentDialog
        isOpen={deleteAssessmentOpen}
        handleClose={handleDeleteAssessmentClose}
        onConfirm={() => handleAssessmentDeleteSubmit(cAssessment?.id as number)}
        disabled={!userCanAdminCompany}
      />
      <AcceptAssessmentDialog
        isOpen={acceptAssessmentOpen}
        handleClose={handleAcceptAssessmentClose}
        onConfirm={handleAcceptAssessment}
        siteCloudStatus={siteCloudStatus}
        surveys={surveys}
      />
      <RejectAssessmentDialog
        isOpen={rejectAssessmentOpen}
        handleClose={handleRejectAssessmentClose}
        onConfirm={handleRejectAssessment}
        disabled={!userCanAdminCompany}
      />
      {((userCanAdminCompany &&
        user?.type === USER_TYPES.VENDOR_ADMIN &&
        ['Assigned', 'Rejected'].indexOf(cAssessment?.status!) !== -1) ||
        user?.type === USER_TYPES.TPN_ADMIN) && (
        <AssessmentForm
          assessment={cAssessment}
          modalOpen={assessmentModalOpen}
          company={company}
          closeCallback={() => setAssessmentModalOpen(false)}
        />
      )}
    </Accordion>
    </>
  );
};

export default AssessmentDataRow;
